import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { ExternalSignInButtons } from "@/components/auth/external-sign-in";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePageTitle } from "@/hooks/use-page-title";
import { authApi } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";

/** Client-side mirror of the API password rule (UX only — the server is authoritative). */
export function passwordProblem(password: string): string | null {
  if (password.length < 8) return "Use at least 8 characters.";
  if (!/[A-Z]/.test(password)) return "Include an uppercase letter.";
  if (!/[a-z]/.test(password)) return "Include a lowercase letter.";
  if (!/[0-9]/.test(password)) return "Include a number.";
  return null;
}

function messageFrom(error: unknown): string {
  if (error instanceof ApiError) {
    const details = (error.data as { error?: { details?: Record<string, string> } } | undefined)
      ?.error?.details;
    const first = details ? Object.values(details)[0] : undefined;
    return first ?? error.message;
  }
  return "Something went wrong. Please try again.";
}

/**
 * Self-serve sign-up. Creates a new CompBox organization with the signer as
 * its admin — or joins their company's organization when it has verified the
 * email domain. No card is required (billing is set up later from the Billing
 * page). Email/password accounts must verify their inbox before signing in;
 * "Continue with Google / Microsoft" signs in straight away.
 */
export function SignupPage() {
  usePageTitle("Sign up");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resendNote, setResendNote] = useState("");

  const submit = async () => {
    setError("");
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await authApi.register({ firstName, lastName, email, password });
      setSentTo(res.email);
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resend = async () => {
    if (!sentTo) return;
    try {
      const res = await authApi.resendVerification(sentTo);
      setResendNote(res.message);
    } catch (err) {
      setResendNote(messageFrom(err));
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-10">
      <BrandMark />
      <h1 className="mt-8 text-3xl font-semibold tracking-tight text-foreground">
        {sentTo ? "Check your inbox" : "Sign up"}
      </h1>

      <div className="mt-8 w-full max-w-md rounded-lg border border-border bg-card p-8">
        {sentTo ? (
          <div className="space-y-4 text-center" role="status">
            <MailCheck className="mx-auto h-10 w-10 text-primary" aria-hidden />
            <p className="text-sm text-foreground">
              We sent a verification link to <span className="font-medium">{sentTo}</span>.
            </p>
            <p className="text-sm text-muted-foreground">
              Click the link in that email to activate your account. It expires in 24 hours.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                void resend();
              }}
            >
              Resend email
            </Button>
            {resendNote && <p className="text-xs text-muted-foreground">{resendNote}</p>}
          </div>
        ) : (
          <div className="space-y-6">
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    autoComplete="given-name"
                    placeholder="Your first name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    autoComplete="family-name"
                    placeholder="Your last name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                    }}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  8+ characters with an uppercase letter, a lowercase letter and a number.
                </p>
              </div>
              {error && (
                <p className="text-sm text-red-400" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Creating account..." : "Continue"}
              </Button>
            </form>

            <ExternalSignInButtons onError={setError} />

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>

      <p className="mt-10 max-w-sm text-center text-xs text-muted-foreground">
        By creating an account, you agree to the{" "}
        <Link to="/terms" className="text-primary hover:underline">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-primary hover:underline">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
